/**
 * Super Admin Announcement Management (MVP — Feb 2026)
 *
 * Surface at /admin/announcements. Backed 100% by existing endpoints:
 *   GET    /api/announcements  (extended with admin-only status_filter +
 *                               include_reminders params)
 *   PUT    /api/announcements/{id}   — edit + archive/unarchive
 *   DELETE /api/announcements/{id}   — hard delete + read-state cleanup
 *
 * Ground rules baked in:
 *   • Audience is historical — never editable after send.
 *   • Editing title/body does not resend push or SMS (backend PUT never
 *     invokes push_service or sms_service).
 *   • Event Reminders are hidden by default; toggle to reveal for audit.
 *     Their Edit + Delete actions are disabled per the approved MVP
 *     decision — Archive is the only safe action so operational reminder
 *     records aren't silently mutated or destroyed.
 *   • Delete is destructive (removes read_states); Archive is the
 *     normal removal path.
 */
import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ParentLayout from '../components/ParentLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Archive, ArchiveRestore, Edit, Trash2, Users, Eye, X,
  AlertTriangle, RefreshCw, Megaphone, Filter,
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { key: 'active', label: 'Active' },
  { key: 'archived', label: 'Archived' },
  { key: 'all', label: 'All' },
];

const AUDIENCE_LABEL = {
  all_users: 'Org-wide (all users)',
  all_parents: 'Org-wide (all parents)',
  all_coaches: 'Org-wide (all coaches)',
  team: 'Team',
  division: 'Division',
  unpaid_balance: 'Unpaid balances',
  incomplete_registration: 'Incomplete registrations',
};

const audienceSummary = (ann) => {
  if (ann.recipient_user_ids && ann.recipient_user_ids.length > 0) {
    return `Custom (${ann.recipient_user_ids.length} recipients)`;
  }
  if (ann.team_id) {
    return `Team: ${ann.team_name || ann.team_id}${ann.recipient_count ? ` — ${ann.recipient_count} recipients` : ''}`;
  }
  if (ann.division_id) {
    return `Division: ${ann.division_id}${ann.recipient_count ? ` — ${ann.recipient_count} recipients` : ''}`;
  }
  const label = AUDIENCE_LABEL[ann.audience_type] || ann.audience_type || 'Unknown';
  return ann.recipient_count ? `${label} — ${ann.recipient_count} recipients` : label;
};

const commTypeBadge = (t) => {
  const map = {
    org_announcement: { label: 'Org', className: 'bg-navy text-white' },
    team_announcement: { label: 'Team', className: 'bg-blue-600 text-white' },
    division_announcement: { label: 'Division', className: 'bg-teal-600 text-white' },
    event_reminder: { label: 'Event Reminder', className: 'bg-orange-500 text-white' },
    system_reminder: { label: 'System', className: 'bg-gray-500 text-white' },
  };
  return map[t] || { label: t || '—', className: 'bg-gray-400 text-white' };
};

export default function AdminAnnouncementsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState('active');
  const [includeReminders, setIncludeReminders] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [audienceRow, setAudienceRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [saving, setSaving] = useState(false);

  // Non-super-admin should never reach this page (route is gated), but
  // guard defensively so a stale token cannot render controls.
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const fetchRows = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        status_filter: statusFilter,
        include_reminders: includeReminders ? 'true' : 'false',
        limit: '500',
      });
      const res = await axios.get(`${API_URL}/api/announcements?${params.toString()}`, {
        withCredentials: true,
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('fetch announcements failed', e);
      setError(e?.response?.data?.detail || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, includeReminders]);

  const counts = useMemo(() => ({
    total: rows.length,
    archived: rows.filter((r) => r.archived).length,
    reminders: rows.filter((r) => r.comm_type === 'event_reminder').length,
  }), [rows]);

  // --- Actions ---
  const handleArchive = async (row) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/announcements/${row.announcement_id}`,
        { archived: true },
        { withCredentials: true },
      );
      await fetchRows();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to archive');
    } finally {
      setSaving(false);
    }
  };

  const handleUnarchive = async (row) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/announcements/${row.announcement_id}`,
        { archived: false },
        { withCredentials: true },
      );
      await fetchRows();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to unarchive');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      // Only send editable fields. Audience is historical — the backend
      // AnnouncementUpdate model ignores audience fields anyway, but we
      // never send them to keep intent explicit.
      const payload = {
        title: editRow.title,
        body: editRow.body,
        priority: editRow.priority,
        is_critical: !!editRow.is_critical,
        is_pinned: !!editRow.is_pinned,
      };
      await axios.put(
        `${API_URL}/api/announcements/${editRow.announcement_id}`,
        payload,
        { withCredentials: true },
      );
      setEditRow(null);
      await fetchRows();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;
    setSaving(true);
    try {
      await axios.delete(
        `${API_URL}/api/announcements/${deleteRow.announcement_id}`,
        { withCredentials: true },
      );
      setDeleteRow(null);
      await fetchRows();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="admin-announcements-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Megaphone className="w-6 h-6 text-navy" />
            <div>
              <h1 className="text-2xl font-bold text-navy">Announcement Management</h1>
              <p className="text-sm text-gray-600">
                Super Admin — edit, archive, or delete communications. Editing does not resend notifications.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={fetchRows}
            data-testid="announcements-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1" role="tablist">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={statusFilter === tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  data-testid={`status-tab-${tab.key}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === tab.key
                      ? 'bg-navy text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Filter className="w-4 h-4 text-gray-500" />
              <label className="text-sm text-gray-700 flex items-center gap-2">
                <span>Include event reminders</span>
                <Switch
                  checked={includeReminders}
                  onCheckedChange={setIncludeReminders}
                  data-testid="include-reminders-toggle"
                />
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {counts.total} announcement{counts.total === 1 ? '' : 's'}
              {includeReminders && counts.reminders > 0 && (
                <span className="text-xs font-normal text-gray-500 ml-2">
                  (includes {counts.reminders} event reminder{counts.reminders === 1 ? '' : 's'})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
            ) : error ? (
              <div className="p-8 text-center text-red-600 text-sm">{error}</div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No announcements for this filter.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-2">Title</th>
                      <th className="text-left px-4 py-2">Type</th>
                      <th className="text-left px-4 py-2">Created</th>
                      <th className="text-left px-4 py-2">Created by</th>
                      <th className="text-left px-4 py-2">Audience</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-right px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const badge = commTypeBadge(r.comm_type);
                      const isReminder = r.comm_type === 'event_reminder';
                      return (
                        <tr
                          key={r.announcement_id}
                          className="border-t hover:bg-gray-50"
                          data-testid={`announcement-row-${r.announcement_id}`}
                        >
                          <td className="px-4 py-3 max-w-xs">
                            <div className="font-medium text-navy truncate">{r.title}</div>
                            {r.is_pinned && <Badge className="mr-1 mt-1 bg-gold text-navy">Pinned</Badge>}
                            {r.is_critical && <Badge className="mt-1 bg-red text-white">Critical</Badge>}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={badge.className}>{badge.label}</Badge>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                            {r.created_at ? format(new Date(r.created_at), 'MMM d, yyyy') : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{r.sender_name || '—'}</td>
                          <td className="px-4 py-3 text-gray-700 max-w-xs truncate">
                            {audienceSummary(r)}
                          </td>
                          <td className="px-4 py-3">
                            {r.archived ? (
                              <Badge className="bg-gray-300 text-gray-800">Archived</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setAudienceRow(r)}
                                title="View audience"
                                data-testid={`action-audience-${r.announcement_id}`}
                              >
                                <Users className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditRow({ ...r })}
                                disabled={isReminder}
                                title={isReminder ? 'Event reminders cannot be edited here' : 'Edit'}
                                data-testid={`action-edit-${r.announcement_id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {r.archived ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUnarchive(r)}
                                  disabled={saving}
                                  title="Unarchive"
                                  data-testid={`action-unarchive-${r.announcement_id}`}
                                >
                                  <ArchiveRestore className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleArchive(r)}
                                  disabled={saving}
                                  title="Archive"
                                  data-testid={`action-archive-${r.announcement_id}`}
                                >
                                  <Archive className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50 border-red-200"
                                onClick={() => setDeleteRow(r)}
                                disabled={isReminder}
                                title={isReminder ? 'Event reminders cannot be deleted here — archive instead' : 'Delete'}
                                data-testid={`action-delete-${r.announcement_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audience History modal */}
        {audienceRow && (
          <Modal onClose={() => setAudienceRow(null)} title="Audience history" testId="audience-modal">
            <div className="space-y-3 text-sm">
              <Row label="Announcement">{audienceRow.title}</Row>
              <Row label="Audience type">{AUDIENCE_LABEL[audienceRow.audience_type] || audienceRow.audience_type || '—'}</Row>
              {audienceRow.team_id && <Row label="Team">{audienceRow.team_name || audienceRow.team_id}</Row>}
              {audienceRow.division_id && <Row label="Division">{audienceRow.division_id}</Row>}
              <Row label="Org-wide">{audienceRow.org_wide ? 'Yes' : 'No'}</Row>
              <Row label="Recipient count (snapshot)">{audienceRow.recipient_count ?? '—'}</Row>
              {Array.isArray(audienceRow.recipient_user_ids) && audienceRow.recipient_user_ids.length > 0 && (
                <Row label="Custom recipient list">
                  {audienceRow.recipient_user_ids.length} explicit user{audienceRow.recipient_user_ids.length === 1 ? '' : 's'}
                </Row>
              )}
              {audienceRow.event_id && <Row label="Linked event">{audienceRow.event_id}</Row>}
              <div className="mt-3 p-3 bg-gray-50 rounded text-xs text-gray-600">
                Audience data reflects what was resolved at send time. It is a historical snapshot and is not
                recomputed from the current roster.
              </div>
            </div>
          </Modal>
        )}

        {/* Edit modal */}
        {editRow && (
          <Modal onClose={() => setEditRow(null)} title="Edit announcement" testId="edit-modal">
            <div className="space-y-3">
              <div className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">
                Editing title or body does not resend push, SMS, or email. Audience is historical and cannot be changed here.
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Title</label>
                <Input
                  value={editRow.title || ''}
                  onChange={(e) => setEditRow({ ...editRow, title: e.target.value })}
                  data-testid="edit-title"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Body</label>
                <Textarea
                  rows={6}
                  value={editRow.body || ''}
                  onChange={(e) => setEditRow({ ...editRow, body: e.target.value })}
                  data-testid="edit-body"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!editRow.is_pinned}
                    onChange={(e) => setEditRow({ ...editRow, is_pinned: e.target.checked })}
                    data-testid="edit-pinned"
                  />
                  Pinned
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!editRow.is_critical}
                    onChange={(e) => setEditRow({ ...editRow, is_critical: e.target.checked })}
                    data-testid="edit-critical"
                  />
                  Critical
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditRow(null)} disabled={saving}>Cancel</Button>
                <Button className="bg-navy text-white" onClick={handleSaveEdit} disabled={saving} data-testid="edit-save">
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Delete confirm modal */}
        {deleteRow && (
          <Modal onClose={() => setDeleteRow(null)} title="Permanently delete announcement?" testId="delete-modal">
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 text-red-800">
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  This will permanently remove <strong>“{deleteRow.title}”</strong> and its read-history records.
                  This cannot be undone. Prefer <strong>Archive</strong> if you want to preserve history.
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDeleteRow(null)} disabled={saving}>Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleConfirmDelete}
                  disabled={saving}
                  data-testid="delete-confirm"
                >
                  {saving ? 'Deleting…' : 'Permanently delete'}
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </ParentLayout>
  );
}

// --- Local UI helpers ---

function Modal({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={testId}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-navy flex items-center gap-2">
            <Eye className="w-4 h-4 text-navy/70" />
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
            aria-label="Close"
            data-testid={`${testId}-close`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-100 pb-1">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium text-navy text-right">{children}</span>
    </div>
  );
}
